/*
 * Grid layout component. Cells can contain other components.
 */

import React from 'react';

import Base from './base.jsx'; 
import {
  castDataType,
  DataType,
} from '../common.jsx';
import Helpers from './helpers.jsx'
import Setting from './setting.jsx';

import Resolver from '../component_resolver.jsx';
import { resolveComponentProps } from '../form_viewer.jsx';
import { DragHandle, DropArea } from '../../common/DragNDrop.jsx';
import { DndProvider } from "react-dnd";
import Backend from "react-dnd-html5-backend";
const DND_ITEM_TYPE = "selection-option";

const VERTICAL_ALIGNMENT_OPTIONS = [
    {
        key: 'start',
        label: I18n.t('top')
    },
    {
        key: 'center',
        label: I18n.t('middle')
    },
    {
        key: 'end',
        label: I18n.t('bottom')
    },
]

export default class GridLayout extends Base {

        static get CATEGORY() {
            return 'layout';
        }

        static get TYPE() {
            return 'grid_layout';
        }

        static icon(){
            return <i className="fa fa-th"></i>;
        }

        static defaultContent(language) {
            return Object.assign(super.defaultContent(language), {
                rows: [
                    [{span: 1, component: null}, {span: 1, component: null}]
                ],
                verticalAlignment: 'top',
                inlineMode: false
            });
        }

        static nestedComponents(parentComponentContent) {
            let components = [];
            parentComponentContent.rows.forEach((row) => {
                row.forEach((col) => {
                    if (col.component && Resolver.componentClass(col.component.type)) {
                        components.push(col.component);
                        components = components.concat(Resolver.componentClass(col.component.type).nestedComponents(col.component));
                    }
                });
            });
            return components;
        }

        static deleteNestedComponent(componentContent, componentId) {
            let found = false;

            componentContent.rows.forEach((row) => {
                row.forEach((col) => {
                    if (col.component && col.component.id == componentId) {
                        delete col.component;
                        found = true;
                    } else if (col.component && Resolver.componentClass(col.component.type).deleteNestedComponent(col.component, componentId)) {
                        found = true;
                    }
                    if (found) { return; }
                });
                if (found) { return; }
            });
            return found;
        }

        static dropComponent(dropArea, targetContent, droppedContent, components, isNested) {
            let dropped = false;

            if (targetContent.id == dropArea.getAttribute('data-component-id') && Resolver.componentClass(droppedContent.type).dropAllowed(dropArea, droppedContent, components, this.isNested() || isNested)) {
                let context = dropArea.getAttribute('data-context').split('-');
                let rowIdx = parseInt(context[0], 10);
                let colIdx = parseInt(context[1], 10);
                targetContent.rows[rowIdx][colIdx].component = JSON.parse(JSON.stringify(droppedContent));
                dropped = true;
            } else {
                this.nestedComponents(targetContent).forEach((c) => {
                    if (Resolver.componentClass(c.type).dropComponent(dropArea, c, droppedContent, components, this.isNested() || isNested)) {
                        dropped = true;
                        return;
                    }
                });
            }
            return dropped;
        }

        constructor(props) {
            super(props);
            this.addRow = this.addRow.bind(this);
            this.deleteRow = this.deleteRow.bind(this);
            this.addColumn = this.addColumn.bind(this);
            this.deleteColumn = this.deleteColumn.bind(this);
            this.reorderOption = this.reorderOption.bind(this);
            this.state = {
                order: [...props?.content?.rows?.keys() || []]
            } 
        }   

        renderComponentCol() {
            return true;
        }

        componentProps() {
            return {};
        }

        renderView() {
            const {content, asWebView} = this.props
            let rows = this.props.content.rows.map((row, rowIdx) => {
                let cols = row.map((col, colIdx) => {
                    let component = null;

                    if (col.component) {
                        col.component.repeatedFieldScope = this.props.repeatedFieldScope;
                        component = Resolver.createElement(col.component.type, 
                          resolveComponentProps(col.component, this.props, {
                            swapComponentsOnDrop: true, 
                            selectCallback: this.props.selectCallback, 
                            selectedId: this.props.selectedId,
                            deleteRepeatableRegionAnswerCallback: this.props.deleteRepeatableRegionAnswerCallback,
                            fieldScope: this.props.fieldScope,
                            repeatedFieldScope: this.props.repeatedFieldScope,
                            ...this.componentProps(col.component),
                          })
                        );
                    } else if (this.inEditMode) {
                        component = (<Helpers.DropArea componentId={this.props.content.id} context={rowIdx + '-' + colIdx} />);
                    }
                    const styles = {
                        flex: content.inlineMode ? 'auto' : col.span,
                        flexBasis: asWebView ? 'min-content' : (content.inlineMode ? 'auto' : 0),
                        overflowX: asWebView ? 'auto' : 'visible'
                    }
                    return (
                        <div
                            key={colIdx}
                            className={`grid-col ${this.renderComponentCol(component) ? "" : "d-none"}`}
                            style={styles}
                            data-flex={col.span}
                            >
                            {component}
                        </div>
                    );
                });
                const gridRowStyle = {
                    flexWrap: asWebView ? 'wrap' : 'nowrap',
                    display: content.inlineMode ? 'inline-flex' : 'flex',
                    alignItems: content.verticalAlignment || 'unset',
                    width: content.inlineMode ? 'auto' : '100%'
                }
                return (
                    <div key={rowIdx} className="grid-row">
                        <div style={gridRowStyle}>
                            {cols}
                        </div>
                    </div>
                );
            });
            return (
                <div className="wrapper">
                    {rows}
                </div>
            );
        }

        renderSettings() {
            let spanOptions = [];
            for (let i = 0; i < 12; i++) {
                spanOptions.push((<option key={i} value={i + 1}>{i + 1}</option>));
            }
            let rows = this.props.content.rows.map((r, ri) => {
                let header = [
                    <td key="0" className="title">{I18n.t('column', {scope: 'fe'})}</td>
                ];
                r.forEach((col, i) => {
                    header.push(
                        <td key={i + 1} className="grid-col title">
                            <span className="number">{i + 1}</span>
                            {(!this.props.safeMode || (col.span && !col.component) || col.component?.isNewComponent == true) &&
                                <a href="#" className="icon-trash delete" title={I18n.t('delete_column', {scope: 'fe'})} data-row-index={ri} data-col-index={i} onClick={this.deleteColumn}></a>
                            }
                        </td>
                    );
                });
                header.push(<td key={header.length}></td>);
                let columns = [
                    <td key="0" className="title">{I18n.t('span', {scope: 'fe'})}</td>
                ];
                r.forEach((col, i) => {
                    columns.push(
                        <td key={i + 1} className="grid-col">
                            <select name={'columns|' + ri + '|' + i + '|span'} defaultValue={col.span} onChange={this.updateSettings} className="form-control" autoComplete={this.getAutoComplete()}>
                                {spanOptions}
                            </select>
                        </td>
                    );
                });
                columns.push(
                    <td key={columns.length} className="buttons">
                        <a href="#" className="icon-add" title={I18n.t('add_column', {scope: 'fe'})} data-row-index={ri} onClick={this.addColumn}></a>
                        {(!this.props.safeMode || r.every(v => (v.span && !v.component) || v.component?.isNewComponent)) &&
                            <a href="#" className="icon-trash" title={I18n.t('delete_row', {scope: 'fe'})} data-row-index={ri} onClick={this.deleteRow}></a>
                        }
                    </td>
                );
                const currentRowId = this.state?.order ? this.state.order[ri] : 0 // error handling in case the order not exist, this will make the re-order don't work but the component still render
                return (
                    <DropArea
                    key={`ri-${currentRowId}`}
                    accept={DND_ITEM_TYPE}
                    item={{ id: `ri-${currentRowId}`, index: ri, type: DND_ITEM_TYPE }}
                    onMove={this.reorderOption}
                    render={({ dropRef, dragRef, isDragging }) => (
                    <div key={`ri-${currentRowId}`} className="grid-row" ref={dropRef} style={{display:'flex'}}>
                        <DragHandle dragRef={dragRef} />
                        <table>
                            <tbody>
                                <tr>{header}</tr>
                                <tr className="columns">{columns}</tr>
                            </tbody>
                        </table>
                    </div>
                )} />
                );
            });
            return (
                <div ref="settings">
                    <div className="setting">
                        <label>
                            {I18n.t('rows', {scope: 'fe'})}
                        </label>
                        <DndProvider backend={Backend} context={window} key={1}>
                            <div className="grid-rows">
                                {rows}
                            </div>
                        </DndProvider>
                    <a href="#" className="btn btn-primary" onClick={this.addRow}><i className="icon-add"></i> {I18n.t('add_row', {scope: 'fe'})}</a>
                    <Setting.Checkbox name="inlineMode" label={I18n.t('field_inline_mode', {scope: 'fe'})} description={I18n.t('field_inline_mode_description', {scope: 'fe'})} value={this.props.content.inlineMode} onChange={this.updateSettings} />
                    <Setting.RadioSelection 
                        name="verticalAlignment" 
                        label={I18n.t('vertical_alignment', {scope: 'fe'})} 
                        selectedOption={this.props.content.verticalAlignment} 
                        onChange={this.updateSettings} 
                        options={VERTICAL_ALIGNMENT_OPTIONS} 
                    />
                    </div>
                </div>
            );
        }

        reorderOption(dragIndex, hoverIndex){
            const rowsCopy = JSON.parse(JSON.stringify(this.props.content.rows));
            const currentOrder = this.state.order;

            const [draggedRow] = rowsCopy.splice(dragIndex, 1);
            const removedOrder = currentOrder.splice(dragIndex, 1)[0];
          
            rowsCopy.splice(hoverIndex, 0, draggedRow);
            currentOrder.splice(hoverIndex,0,removedOrder)
            this.setState({order: currentOrder})

            this.setState({order: currentOrder})
            const updatedContent = { id: this.props.content.id, rows: rowsCopy };
            
            this.props.updateSettingsCallback(updatedContent.id,updatedContent,true);
          }

        addRow(event) {
            let content = {id: this.props.content.id};
            let nextRowIndex = this.props.content.rows.length;
            let lastRow = this.props.content.rows[nextRowIndex - 1];

            jQuery(this.refs[this.settingsFormRef()]).find('.grid-col select').serializeArray().forEach((s) => {
                content[s.name] = s.value;
            });

            for (let colIdx = 0; colIdx < (lastRow ? lastRow.length : 2); colIdx++) {
                content['columns|' + nextRowIndex + '|' + colIdx + '|span'] = lastRow ? lastRow[colIdx].span : 1;
            }

            let addedOrder = this.state.order;
            addedOrder.push(Math.max(...this.state.order) + 1);
            this.setState({order: addedOrder});
            this.beforeUpdateSettings(content);
            this.props.updateSettingsCallback(content.id, content, true);

            event.preventDefault();
        }

        deleteRow(event) {
            if (this.props.content.rows.length < 2) {
                alert(I18n.t('alert_row_deletion', {scope: 'fe'}));
            } else if (confirm(I18n.t('confirm_delete_grid_row', {scope: 'fe'}))) {
                let content = {id: this.props.content.id};
                let deletedRow = jQuery(event.currentTarget).closest('.grid-row');

                jQuery(this.refs[this.settingsFormRef()]).find('.grid-row').not(deletedRow).each((i, row) => {
                    jQuery(row).find('select').serializeArray().forEach((s) => {
                        content[s.name.replace(/columns\|[0-9]+\|/, 'columns|' + i + '|')] = s.value;
                    });
                });

                let rowIdx = parseInt(event.currentTarget.getAttribute('data-row-index'), 10);
                let currentOrder = this.state.order;
                currentOrder.splice(rowIdx,1);
                this.setState({order: currentOrder});
                this.beforeUpdateSettings(content, rowIdx);
                this.props.updateSettingsCallback(content.id, content, true);
            }
            event.preventDefault();
        }


        addColumn(event) {
            let content = {id: this.props.content.id};
            let rowIdx = parseInt(event.currentTarget.getAttribute('data-row-index'), 10);
            let nextColIndex = this.props.content.rows[rowIdx].length;

            jQuery(this.refs[this.settingsFormRef()]).find('.grid-col select').serializeArray().forEach((s) => {
                content[s.name] = s.value;
            });
            content['columns|' + rowIdx + '|' + nextColIndex + '|span'] = '1';

            this.beforeUpdateSettings(content);
            this.props.updateSettingsCallback(content.id, content, true);

            event.preventDefault();
        }

        deleteColumn(event) {
            let rowIdx = parseInt(event.currentTarget.getAttribute('data-row-index'), 10);
            let colIdx = parseInt(event.currentTarget.getAttribute('data-col-index'), 10);

            if (this.props.content.rows[rowIdx].length < 2) {
                alert(I18n.t('alert_column_deletion', {scope: 'fe'}));
            } else if (confirm(I18n.t('confirm_delete_grid_col', {scope: 'fe'}))) {
                let content = {id: this.props.content.id};
                let ignoredSelect = jQuery(this.refs[this.settingsFormRef()]).find('.grid-row:eq(' + rowIdx + ') .columns .grid-col:eq(' + colIdx + ') select');

                jQuery(this.refs[this.settingsFormRef()]).find('.grid-row').each((ri, row) => {
                    jQuery(row).find('.grid-col select').not(ignoredSelect).serializeArray().forEach((s, i) => {
                        content[s.name.replace(/\|([0-9]+)\|[0-9]+\|/, '|$1|' + i + '|')] = s.value;
                    });
                });

                this.beforeUpdateSettings(content, rowIdx, colIdx);
                this.props.updateSettingsCallback(content.id, content, true);
            }
            event.preventDefault();
        }

        beforeUpdateSettings(content, deletedRowIndex, deletedColIndex) {
            content.rows = [];

            Object.keys(content).filter((k) => { return k.match(/^columns\|/); }).forEach((k) => {
                let parts = k.split('|'); // columns|rowIdx|colIdx|attr
                let rowIdx = parseInt(parts[1], 10);
                let oldRowIdx = (typeof deletedRowIndex != 'undefined' && rowIdx >= deletedRowIndex && typeof deletedColIndex == 'undefined') ? rowIdx + 1 : rowIdx;
                let colIdx = parseInt(parts[2], 10);
                let oldColIdx = (typeof deletedColIndex != 'undefined' && rowIdx == deletedRowIndex && colIdx >= deletedColIndex) ? colIdx + 1 : colIdx;
                let attr = parts[3]
                content.rows[rowIdx] = content.rows[rowIdx] || [];
                content.rows[rowIdx][colIdx] = {component: ((this.props.content.rows[oldRowIdx] || [])[oldColIdx] || {}).component};
                content.rows[rowIdx][colIdx][attr] = castDataType(content[k], DataType.INTEGER);
                delete content[k];
            });
            content.inlineMode = castDataType(content.inlineMode, DataType.BOOLEAN);
        }
}
